import React, { useState, useEffect } from 'react';
import { Users, Calendar, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { differenceInDays } from 'date-fns';

import axios from 'axios';
import { getApiUrl } from "../../config/api";
import { useSubscription } from '../../hooks/useSubscription';

export default function MembershipWidget({ config = {} }) {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { hasFeature } = useSubscription();


  const { 
    title = 'Memberships',
    maxMemberships = 5,
    showExpiringSoon = true,
    showExpired = false,
    showActive = true
  } = config;

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('/api/associations/memberships'), {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });

      if (response.data) {
        // Sort by expiration date (closest to expiring first)
        const sortedMemberships = response.data.sort((a, b) => {
          const dateA = new Date(a.expiration_date);
          const dateB = new Date(b.expiration_date);
          return dateA - dateB;
        });
        
        setMemberships(sortedMemberships);
      }
    } catch (error) {
      console.error('Error fetching memberships:', error);
      setError('Failed to load memberships');
    } finally {
      setLoading(false);
    }
  };

  const getMembershipStatus = (expirationDate) => {
    const today = new Date();
    const expDate = new Date(expirationDate);
    const daysUntilExpiry = differenceInDays(expDate, today);
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', color: 'text-red-600', bgColor: 'bg-red-100', icon: AlertTriangle };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring-soon', color: 'text-orange-600', bgColor: 'bg-orange-100', icon: Clock };
    } else {
      return { status: 'active', color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircle };
    }
  };

  const getMembershipStatusText = (expirationDate) => {
    const today = new Date();
    const expDate = new Date(expirationDate);
    const daysUntilExpiry = differenceInDays(expDate, today);
    
    if (daysUntilExpiry < 0) {
      return `Expired ${Math.abs(daysUntilExpiry)} days ago`;
    } else if (daysUntilExpiry === 0) {
      return 'Expires today';
    } else if (daysUntilExpiry === 1) {
      return 'Expires tomorrow';
    } else if (daysUntilExpiry <= 30) {
      return `Expires in ${daysUntilExpiry} days`;
    } else {
      return `Expires in ${daysUntilExpiry} days`;
    }
  };

  const filterMemberships = () => {
    let filtered = memberships;
    
    if (!showExpired) {
      filtered = filtered.filter(m => new Date(m.expiration_date) >= new Date());
    }
    
    if (!showExpiringSoon) {
      filtered = filtered.filter(m => {
        const daysUntilExpiry = differenceInDays(new Date(m.expiration_date), new Date());
        return daysUntilExpiry > 30;
      });
    }
    
    if (!showActive) {
      filtered = filtered.filter(m => new Date(m.expiration_date) < new Date());
    }
    
    return filtered.slice(0, maxMemberships);
  };



  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  const filteredMemberships = filterMemberships();

  if (filteredMemberships.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-gray-500 text-sm">No memberships found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      
      <div className="space-y-3">
        {filteredMemberships.map((membership) => {
          const status = getMembershipStatus(membership.expiration_date);
          const StatusIcon = status.icon;
          
          return (
            <div key={membership.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <StatusIcon className={`h-4 w-4 ${status.color}`} />
                  <h4 className="font-medium text-gray-900 truncate">
                    {membership.association_name}
                  </h4>
                </div>
                {hasFeature('family_plan') && membership.owner_name && (
                  <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">{membership.owner_name}</div>
                )}
                
                {membership.membership_title && (
                  <p className="text-sm text-gray-600 mb-1">
                    {membership.membership_title}
                  </p>
                )}
                
                {/* Desktop: Show duration, Mobile: Show membership number */}
                <div className="hidden md:flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="h-3 w-3" />
                  <span>{getMembershipStatusText(membership.expiration_date)}</span>
                </div>
                
                {/* Mobile: Show membership number instead of duration */}
                {membership.membership_number && (
                  <div className="md:hidden flex items-center gap-2 text-xs text-gray-500">
                    <span># {membership.membership_number}</span>
                  </div>
                )}
                
                {/* Desktop: Show membership number below duration */}
                {membership.membership_number && (
                  <p className="hidden md:block text-xs text-gray-500 mt-1">
                    # {membership.membership_number}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2 ml-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                  {status.status === 'expired' ? 'Expired' : 
                   status.status === 'expiring-soon' ? 'Expiring Soon' : 'Active'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      

    </div>
  );
}
